import React from 'react';
import queryString from 'query-string';
import { useDispatch, useSelector } from 'react-redux';
import { ArrayHelpers, Box, Loading, SystemText, SaveButton, DebugInfo } from 'e4shared';

import PageHeader from 'components/general/pageHeader';
import NoResults from 'components/general/noResults';

import { apiGet, apiPost, apiDownloadFile } from 'services';

import Reviews from './components/reviews';
import Schedule from './components/schedule';
import { setAsideFilters, setShowSuccess, toggleFilterAside } from 'actions/ui';

import InfoBox from 'components/general/infoBox';
import SearchField from 'components/general/searchField';

import posthog from 'posthog-js';

export const ReviewTracker = ({ location }) => {
    const dispatch = useDispatch();

    const [loading, setLoading] = React.useState(true);
    const [searchFilter, setSearchFilter] = React.useState('');
    const [page, setPage] = React.useState(1);
    const [sortBy, setSortBy] = React.useState('submission');
    const [descending, setDescending] = React.useState(true);
    const [recordCount, setRecordCount] = React.useState(0);
    const [data, setData] = React.useState(null);
    const [selected, setSelected] = React.useState([]);
    const [selectAll, setSelectAll] = React.useState(false);
    const [showSchedule, setShowSchedule] = React.useState(false);
    const [users, setUsers] = React.useState([]);
    const [unreviewedShown, setUnreviewedShown] = React.useState(false);
    const [downloadingReport, setDownloadingReport] = React.useState(false);
    const [status, setStatus] = React.useState(null);

    const reviewTypes = useSelector(state => state.user.user.reviewTypes) || [];
    const hasManagerReviews = reviewTypes.some(x => x.indexOf('M') >= 0);

    const { filterAsideOpen, asideFilters } = useSelector(state => state.ui);
    const user = useSelector(state => state.user.user);
    const allowCoaches = user.companySettings.development.allowCoaches;

    const { unreviewed } = queryString.parse(location.search);

    const api = {
        loadUsers: async () => {
            await apiGet('review-users', user.guid, response => {
                setUsers(response);
            });
        },
        loadReviewDashboard: async callback => {
            const filters = asideFilters
                .map(x => ({ property: x.property, value: x.value }))
                .concat([{ property: 'search', value: searchFilter }]);

            const qs = `?page=${page}&status=${status}&sort=${sortBy}&desc=${descending}`;

            await apiPost(
                `review-dashboard${qs}`,
                filters,
                user.guid,
                response => {
                    setData(response);
                    setRecordCount(response.recordcount);
                    callback?.();
                },
                null,
                'POST',
                'json'
            );
        },
        assign: (details, callback) => {
            apiPost('review-assign', details, user.guid, () => {
                dispatch(setShowSuccess(SystemText('ReviewTracker/Tabs/Reviews/Success')));
                setShowSchedule(false);
                callback();
                api.loadReviewDashboard();
                api.loadUsers();
            });
        },
        report: () => {
            const filters = asideFilters
                .map(x => ({ property: x.property, value: x.value }))
                .concat([{ property: 'search', value: searchFilter }]);

            apiDownloadFile(
                'review-report',
                filters,
                user.guid,
                () => {
                    setDownloadingReport(false);
                    posthog.capture('tracker', { trackerType: 'ReviewTracker', userEvent: 'export' });
                },
                null,
                'POST'
            );
        },
    };

    React.useEffect(() => {
        const promises = [];

        promises.push(api.loadReviewDashboard());
        promises.push(api.loadUsers());

        if (!promises.length) {
            setLoading(false);
        } else {
            Promise.all(promises).then(() => {
                setLoading(false);
            });
        }
    }, []);

    React.useEffect(() => {
        let fields = [
            {
                property: 'role_Ids',
                name: SystemText('ReviewTracker/Filters/Role'),
                type: 'list',
            },
            {
                property: 'department_Ids',
                name: SystemText('ReviewTracker/Filters/Department'),
                type: 'list',
            },
            {
                property: 'business_Unit_Ids',
                name: SystemText('ReviewTracker/Filters/BusinessUnit'),
                type: 'list',
            },
            {
                property: 'location_Ids',
                name: SystemText('ReviewTracker/Filters/Location'),
                type: 'list',
            },
            {
                property: 'language_Ids',
                name: SystemText('ReviewTracker/Filters/Language'),
                type: 'list',
            },
            {
                property: 'statuses',
                name: SystemText('ReviewTracker/Filters/Status'),
                type: 'list',
                stringValues: true,
                data: [
                    { id: 'O', name: SystemText('ReviewTracker/Filters/Status/Outstanding') },
                    { id: 'U', name: SystemText('ReviewTracker/Filters/Status/User') },
                    { id: 'M', name: SystemText('ReviewTracker/Filters/Status/Manager') },
                    { id: 'C', name: SystemText('ReviewTracker/Filters/Status/Complete') },
                    { id: 'D', name: SystemText('ReviewTracker/Filters/Status/Overdue') },
                ],
            },
            {
                property: 'manager_Ids',
                name: SystemText('ReviewTracker/Filters/Manager'),
                type: 'list',
                nameProperty: 'list_Name',
            },
            {
                property: 'coach_Ids',
                name: SystemText('ReviewTracker/Filters/Coach'),
                type: 'list',
                nameProperty: 'list_Name',
            },
            {
                property: 'due',
                name: SystemText('ReviewTracker/Filters/Due'),
                type: 'range',
            },
            {
                property: 'submission',
                name: SystemText('ReviewTracker/Filters/Completed'),
                type: 'range',
            },
            {
                property: 'created',
                name: SystemText('ReviewTracker/Filters/Creation'),
                type: 'range',
            },
            {
                property: 'launch_Date',
                name: SystemText('ReviewTracker/Filters/Launch'),
                type: 'range',
            },
            {
                property: 'participants',
                name: SystemText('ReviewTracker/Filters/Type'),
                type: 'list',
                stringValues: true,
                data: [
                    { id: 'U', name: SystemText('ReviewTracker/Filters/Type/User') },
                    { id: 'M', name: SystemText('ReviewTracker/Filters/Type/Manager') },
                    { id: 'UM', name: SystemText('ReviewTracker/Filters/Type/Both') },
                ],
            },
            {
                property: 'owner_Ids',
                name: SystemText('ReviewTracker/Filters/Assigned'),
                type: 'list',
                nameProperty: 'list_Name',
            },
        ];

        if (!allowCoaches) {
            fields = fields.filter(x => x.property !== 'coach_Ids');
        }

        dispatch(setAsideFilters(fields));
    }, []);

    React.useEffect(() => {
        if (ArrayHelpers.existsAndHasLength(users) && unreviewed && !unreviewedShown) {
            setShowSchedule(true);
            setUnreviewedShown(true);
        }
    }, [unreviewed, unreviewedShown, users]);

    const sortProps = {
        sortBy,
        descending,
        setSortBy,
        setDescending,
    };

    React.useEffect(() => {
        const temp = selectAll ? data.allIncompleteIds : [];
        setSelected(temp);
    }, [selectAll]);

    React.useEffect(() => {
        if (!filterAsideOpen) {
            setPage(1);
            setLoading(true);
            api.loadReviewDashboard(() => setLoading(false));
        }
    }, [filterAsideOpen]);

    React.useEffect(() => {
        setPage(1);
        setLoading(true);
        api.loadReviewDashboard(() => setLoading(false));
    }, [searchFilter, status, sortBy, descending]);

    React.useEffect(() => {
        setLoading(true);
        api.loadReviewDashboard(() => setLoading(false));
    }, [page]);

    React.useEffect(() => {
        DebugInfo('component', 'ReviewTracker');
        posthog.capture('tracker', { trackerType: 'ReviewTracker', userEvent: 'view' });
    }, []);

    const extraButtons = () => (
        <button className="btn btn-success" onClick={() => setShowSchedule(true)}>
            <i className="mr10 fa fa-users" />
            {SystemText('ReviewTracker/Buttons/Assign')}
        </button>
    );

    const showFilterMenu = () => {
        dispatch(toggleFilterAside(true));
    };

    return (
        <div>
            <PageHeader title={SystemText('ReviewTracker/Title')} extra={extraButtons} tracker />
            <section className="content mtm10 tracker" data-testid="review-tracker-content">
                <Box>
                    <div className="mb10">
                        <SearchField
                            value={searchFilter}
                            onChange={setSearchFilter}
                            placeholder={SystemText('ReviewTracker/Search')}
                        >
                            {(user.administrator || user.isManager) && (
                                <SaveButton
                                    className="btn btn-info mr10"
                                    saving={downloadingReport}
                                    setSaving={setDownloadingReport}
                                    onClick={api.report}
                                >
                                    <i className="fa fa-download mr5" />
                                    {SystemText('ReviewTracker/Buttons/Report')}
                                </SaveButton>
                            )}
                            <button className="btn btn-success" id="search-filter-button" onClick={showFilterMenu}>
                                <i className="fa fa-bars-filter mr5" id="search-filter-button-icon" />
                                {SystemText('ReviewTracker/Buttons/Filter')}
                            </button>
                        </SearchField>
                    </div>
                    <div className="info-box-row mb20">
                        <InfoBox className="" selected={status === ''} onClick={() => setStatus('')}>
                            <div className="flex flex-column flex-center">
                                <div className="text-fit">{SystemText('ReviewTracker/Tabs/Details/Scheduled')}</div>
                                <div>{!loading ? <span>{data?.scheduled}</span> : <Loading />}</div>
                            </div>
                        </InfoBox>
                        <InfoBox className="success" selected={status === 'C'} onClick={() => setStatus('C')}>
                            <div className="flex flex-column flex-center">
                                <div className="text-fit">{SystemText('ReviewTracker/Tabs/Details/Completed')}</div>
                                <div>{!loading ? <span>{data?.completed}</span> : <Loading />}</div>
                            </div>
                        </InfoBox>
                        {hasManagerReviews && (
                            <>
                                <InfoBox className="warning" selected={status === 'M'} onClick={() => setStatus('M')}>
                                    <div className="flex flex-column flex-center">
                                        <div className="text-fit">
                                            {SystemText('ReviewTracker/Tabs/Details/AwaitingManager')}
                                        </div>
                                        <div>{!loading ? <span>{data?.awaitingManager}</span> : <Loading />}</div>
                                    </div>
                                </InfoBox>
                                <InfoBox className="warning" selected={status === 'U'} onClick={() => setStatus('U')}>
                                    <div className="flex flex-column flex-center">
                                        <div className="text-fit">
                                            {SystemText('ReviewTracker/Tabs/Details/AwaitingUser')}
                                        </div>
                                        <div>{!loading ? <span>{data?.awaitingUser}</span> : <Loading />}</div>
                                    </div>
                                </InfoBox>
                            </>
                        )}
                        <InfoBox className="danger" selected={status === 'O'} onClick={() => setStatus('O')}>
                            <div className="flex flex-column flex-center">
                                <div className="text-fit">{SystemText('ReviewTracker/Tabs/Details/Outstanding')}</div>
                                <div>{!loading ? <span>{data?.outstanding}</span> : <Loading />}</div>
                            </div>
                        </InfoBox>
                        <InfoBox className="danger" selected={status === 'D'} onClick={() => setStatus('D')}>
                            <div className="flex flex-column flex-center">
                                <div className="text-fit">{SystemText('ReviewTracker/Tabs/Details/Overdue')}</div>
                                <div>{!loading ? <span>{data?.overdue}</span> : <Loading />}</div>
                            </div>
                        </InfoBox>
                    </div>
                    <div>
                        {loading ? (
                            <Loading />
                        ) : recordCount ? (
                            <Reviews
                                data={data}
                                page={page}
                                users={users}
                                status={status}
                                setStatus={setStatus}
                                loading={loading}
                                setPage={setPage}
                                selected={selected}
                                setSelected={setSelected}
                                selectAll={selectAll}
                                setSelectAll={setSelectAll}
                                {...sortProps}
                                reload={() => {
                                    setSelectAll(false);
                                    setSelected([]);
                                    api.loadReviewDashboard();
                                    api.loadUsers();
                                }}
                            />
                        ) : (
                            <NoResults
                                title={SystemText('ReviewTracker/NoResults/Title')}
                                message={SystemText('ReviewTracker/NoResults/Message')}
                            />
                        )}
                    </div>
                </Box>
            </section>
            <Schedule
                show={showSchedule}
                unreviewed={unreviewed}
                users={users}
                close={() => {
                    setShowSchedule(false);
                }}
                onSchedule={api.assign}
            />
        </div>
    );
};

export default ReviewTracker;
